/**
 * @file ReceiptBulkAddButton.tsx
 * @description Массовый подбор фасовок в приёмку (phase19.13 этап 0).
 *
 * Кнопка + пикер + прогресс: выбранные фасовки добавляются позициями по 1 шт,
 * количество и цены кладовщик правит в карточке. Логика вынесена из страницы,
 * чтобы та не держала состояние диалога и цикла добавления.
 *
 * Единственное место с галочками: «Подбор» в панели документа с phase19.24
 * этапа 1 выбирает ОДНУ фасовку (`WmsSkuSelectDialog`), а массовое добавление
 * живёт здесь, в заголовке «Позиции», и называется «Добавить несколько».
 */

import { ListPlus, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import { WmsSkuPickerDialog, type WmsPickerSku } from "./WmsSkuPickerDialog";
import { ApiError } from "../../lib/api-client";
import { receiptsApi } from "../../lib/api-wms";

interface Props {
  receiptId: string;
  /** Уже добавленные skuId — в пикере disabled. */
  alreadyAddedIds: string[];
  /** Вызывается после успешного добавления — страница перечитывает документ. */
  onAdded: () => Promise<void> | void;
}

/** Сегодня в формате YYYY-MM-DD. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Сегодня + N дней. */
function todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function ReceiptBulkAddButton({
  receiptId,
  alreadyAddedIds,
  onAdded,
}: Props) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSelect(picked: WmsPickerSku[]) {
    setOpen(false);
    if (picked.length === 0) return;
    setBusy(true);
    try {
      for (const sku of picked) {
        await receiptsApi.addItem(receiptId, {
          skuId: sku.id,
          quantity: "1",
          manufacturedAt: today(),
          expiresAt: todayPlus(365),
        });
      }
      await onAdded();
      notify.success(`Добавлено: ${picked.length}`);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="min-h-[48px]"
      >
        <ListPlus className="size-4" />
        <span className="ml-1.5">Добавить несколько</span>
      </Button>

      <WmsSkuPickerDialog
        open={open}
        onClose={() => setOpen(false)}
        onSelectMany={(picked) => void handleSelect(picked)}
        alreadyAddedIds={alreadyAddedIds}
      />

      {busy ? (
        <div className="fixed inset-0 z-[var(--z-dialog)] flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-surface-lowest px-6 py-4 shadow-xl">
            <Loader2 className="inline size-4 animate-spin text-primary" />
            <span className="ml-2 text-sm">Добавляем позиции…</span>
          </div>
        </div>
      ) : null}
    </>
  );
}
